/**
 * NotificationPermissionPrompt
 *
 * A dismissible banner that invites the user to enable push notifications.
 * Shown once: after their first chart is created (onboarding complete),
 * not on the first visit. Uses localStorage to remember dismissal.
 *
 * Conditions to show:
 *  - User is authenticated and has completed birth profile (birthDate set)
 *  - Push is supported by the browser
 *  - Permission has not been granted or denied yet (state === 'default')
 *  - User has not already dismissed the prompt ('notif-prompt-seen' not set)
 */
import { useState, useEffect } from 'react'
import { Bell, X, BellOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUser } from '@/store'
import { useNotifications } from '@/hooks/useNotifications'

const DISMISSED_KEY = 'shubh-notif-prompt-dismissed'

export function NotificationPermissionPrompt() {
  const user = useUser()
  const { permissionState, isSupported, requestPermission } = useNotifications()

  const [visible, setVisible] = useState(false)
  const [requesting, setRequesting] = useState(false)

  useEffect(() => {
    if (
      user?.profile?.dateOfBirth &&  // onboarding complete
      isSupported &&
      permissionState === 'default' &&
      !localStorage.getItem(DISMISSED_KEY)
    ) {
      // Small delay so the dashboard renders first
      const t = setTimeout(() => setVisible(true), 1200)
      return () => clearTimeout(t)
    }
  }, [user?.profile?.dateOfBirth, isSupported, permissionState])

  if (!visible) return null

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1')
    setVisible(false)
  }

  async function handleEnable() {
    setRequesting(true)
    const result = await requestPermission()
    setRequesting(false)
    // Hide regardless of outcome — denied shows info in Settings
    localStorage.setItem(DISMISSED_KEY, '1')
    setVisible(false)
    if (result === 'denied') {
      // No further action — user can revisit in Settings
    }
  }

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        'w-full max-w-md mx-auto px-4',
        'animate-fade-in',
      )}
      role="dialog"
      aria-label="Enable push notifications"
    >
      <div className="card-cosmic p-4 shadow-card flex items-start gap-3">
        {/* Icon */}
        <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bell className="w-4 h-4 text-gold" />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <p className="text-star-bright text-sm font-semibold">
            Get daily auspicious timings
          </p>
          <p className="text-star-muted text-2xs mt-0.5 leading-relaxed">
            Receive morning Panchang summaries and Rahu Kaal alerts so you never miss an auspicious window.
          </p>

          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleEnable}
              disabled={requesting}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold',
                'bg-gold/20 border border-gold/30 text-gold',
                'hover:bg-gold/30 transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              {requesting ? (
                <span className="w-3 h-3 rounded-full border border-gold border-t-transparent animate-spin" />
              ) : (
                <Bell className="w-3 h-3" />
              )}
              {requesting ? 'Requesting…' : 'Enable notifications'}
            </button>

            <button
              onClick={dismiss}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-star-dim hover:text-star-soft transition-colors"
            >
              <BellOff className="w-3 h-3" />
              Not now
            </button>
          </div>
        </div>

        {/* Close */}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-star-dim hover:text-star-soft transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
